import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateRange } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isNullOrUndefined } from '@sla-ng/core';
import * as moment from 'moment';
import { BehaviorSubject, filter, Observable, take } from 'rxjs';
import { ISharedDataState } from '../../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../../services/facades/master-data/shared-data.facade';
import { IAddPlannedSaleVolume } from '../../../../models/add-planned-sale-volume.model';
import { IPlannedSaleVolumeDialogLayout } from '../../../../models/layout/planned-sale-volume-dialog-layout.model';
import { IPlannedSaleVolume } from '../../../../models/planned-sale-volume.model';
import { IUpdatePlannedSaleVolume } from '../../../../models/update-planned-sale-volume.model';
import { IPlannedSaleVolumeState } from '../../../../services/facades/planning/planned-sale-volume-state.model';
import { PlannedSaleVolumeFacade } from '../../../../services/facades/planning/planned-sale-volume.facade';
import { PlannedSaleVolumeLayoutService } from '../../../../services/layout/planned-sale-volume-layout.service';
import { stringToMoment } from '../../../../utils/moment-utils';

@Component({
    templateUrl: './planned-sale-volume-dialog.component.html',
    styleUrls: [ './planned-sale-volume-dialog.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlannedSaleVolumeDialogComponent implements OnInit {
    public state$: Observable<IPlannedSaleVolumeState>;
    public dialogFieldsValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public formGroup$: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);
    public layout: IPlannedSaleVolumeDialogLayout;
    public initialData: BehaviorSubject<{ dateRange: DateRange<moment.Moment>, amount: number, measureUnitId: number, costUnitId: number }>
        = new BehaviorSubject<{ dateRange: DateRange<moment.Moment>, amount: number, measureUnitId: number, costUnitId: number }>({
            dateRange: undefined,
            amount: undefined,
            costUnitId: undefined,
            measureUnitId: undefined,
        },
        );

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IPlannedSaleVolume,
        private myMatDialogRef: MatDialogRef<PlannedSaleVolumeDialogComponent>,
        private myPlannedSaleVolumeFacade: PlannedSaleVolumeFacade,
        private myLayoutService: PlannedSaleVolumeLayoutService,
        private mySharedDataFacade: SharedDataFacade
    ) {
    }

    public ngOnInit(): void {
        this.state$ = this.myPlannedSaleVolumeFacade.subState();
        if (!isNullOrUndefined(this.data) && !isNullOrUndefined(this.data.id)) {
            this.layout = this.myLayoutService.getUpdateDialogLayout();
            this.initialData.next({
                dateRange: new DateRange( stringToMoment(this.data.validFrom), stringToMoment(this.data.validTo)),
                amount: this.data.amount,
                measureUnitId: this.data.costUnit.baseMeasureUnitId,
                costUnitId: this.data.costUnit.id
            });
        } else {
            this.layout = this.myLayoutService.getAddDialogLayout();
        }


        if (!isNullOrUndefined(this.data.validFrom) && !isNullOrUndefined(this.data.validTo)) {
            this.formGroup$.asObservable()
                .pipe(
                    filter(value => !isNullOrUndefined(value)),
                    take(1),
                ).subscribe(() => {
                    this.formGroup$.value.controls[ 'dateRange' ].patchValue({ start: this.data.validFrom, end: this.data.validTo }, { emitEvent: false });
                });
        }

    }

    public async save(): Promise<void> {
        const data: IAddPlannedSaleVolume = this.transformDialogData(this.formGroup$.value.getRawValue());
        if (isNullOrUndefined(this.data.id)) {
            await this.myPlannedSaleVolumeFacade.addPlannedSaleVolume(data);
        } else {
            await this.myPlannedSaleVolumeFacade.updatePlannedSaleVolume(this.data.id, data as IUpdatePlannedSaleVolume);
        }
        this.close();
    }

    public close(): void {
        this.myMatDialogRef.close(false);
    }

    public onFormGroupChange(formGroup: FormGroup): void {
        formGroup.valueChanges.subscribe((data: { dateRange: DateRange<moment.Moment>, amount: number, measureUnitId: number, costUnitId: number }) => {
            this.validateDialogFields(this.transformDialogData(data));
        });

        formGroup.controls[ 'costUnitId' ].valueChanges.subscribe((costUnitId: number) => {
            this.selectMeasureUnit(costUnitId);
        });

        this.formGroup$.next(formGroup);
    }

    private validateDialogFields(plannedPurchasePrice: IAddPlannedSaleVolume): void {
        // Validations to disable saving/editing
        if ( isNullOrUndefined(this.data.id) ){
            // 'Datensatz' existiert so vorher noch nicht - alles angeben
            if (isNullOrUndefined(plannedPurchasePrice.costUnitId)
                || isNullOrUndefined(plannedPurchasePrice.amount)
                || plannedPurchasePrice.amount <= 0
                || isNullOrUndefined(plannedPurchasePrice.from)
                || isNullOrUndefined(plannedPurchasePrice.to)
                || plannedPurchasePrice.to.isBefore(plannedPurchasePrice.from)) {
                this.dialogFieldsValid$.next(false);
            } else {
                this.dialogFieldsValid$.next(true);
            }
        } else {
            if ( isNullOrUndefined(plannedPurchasePrice.amount)
            || isNullOrUndefined(plannedPurchasePrice.from)
            || isNullOrUndefined(plannedPurchasePrice.to) ){
                // amount wird mit Wert im Bearbeiten-Modus angegeben - kein Mengenwert mehr
                this.dialogFieldsValid$.next(false);
            } else {
                this.dialogFieldsValid$.next(true);
            }
        }
    }

    private transformDialogData(data: { dateRange: DateRange<moment.Moment | string>, amount: number, measureUnitId: number | string, costUnitId: number }):
    IAddPlannedSaleVolume {
        return {
            costUnitId: isNullOrUndefined(data.costUnitId) ? this.data.costUnit.id : data.costUnitId,
            amount: data.amount,
            measureUnitId: typeof data.measureUnitId === 'string' ? undefined : data.measureUnitId,
            from: stringToMoment(data.dateRange.start),
            to: stringToMoment(data.dateRange.end),
        };
    }
    private selectMeasureUnit(costUnitId: number): void {


        const sharedDataState: ISharedDataState = this.mySharedDataFacade.getStateSnapshot();
        const measureUnitId: number = sharedDataState.measureUnitLookup[ sharedDataState.costUnitLookup[ costUnitId ]?.baseMeasureUnitId ]?.id;

        this.formGroup$.value.controls[ 'measureUnitId' ].patchValue(measureUnitId, { emitEvent: false });
    }
}



